import {
  Box,
  Hide,
  SimpleGrid,
  Stack,
  Text,
  useRadioGroup,
} from '@chakra-ui/react';
import { Divider, Grid, gridConfig } from '@time-line/components/common';
import { PromoCode } from 'components/common/PromoCode';
import {
  ProductBulletPoints,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { ProductCheckmarks } from 'components/product/ProductCheckmarks';
import { ProductConfiguratorRender } from 'components/product/ProductConfiguratorRender';
import { ProductDisclaimer } from 'components/product/ProductDisclaimer';
import { ProductFrontrowMdWidget } from 'components/product/ProductFrontrowMdWidget';
import { ProductGalleryDesktop } from 'components/product/ProductGallery/ProductGalleryDesktop';
import { ProductGalleryMobile } from 'components/product/ProductGallery/ProductGalleryMobile';
import { ProductIntro } from 'components/product/ProductIntro';
import { ProductSeeItInAction } from 'components/product/ProductSeeItInAction';
import { RadioCard } from 'components/ui/RadioCard';
import { useProductsUpdatedPrices } from 'hooks/product/useProductsUpdatedPrices';
import { env } from 'lib/constants/env';
import { useMemo, useState } from 'react';
import type { SupplementPicker as SupplementPickerProps } from 'types/cms/blocks';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { VariantOrSubscription } from 'types/product/VariantOrSubscription';
import { resolveProductInfo } from 'utils/product/resolveProductInfo';

import { findMatchingVariantOrSub } from './utils/findMatchingVariantOrSub';
import { useGetProductData } from './utils/useGetProductData';

const { borderWidthClamp } = gridConfig;

export function SupplementPicker({
  _key,
  products,
  ctaType,
  purchaseType,
  amazonButton,
  promoCodes,
}: SupplementPickerProps) {
  const [selectedProductIndex, setSelectedProductIndex] = useState<number>(0);
  const [selected, setSelected] = useState<VariantOrSubscription | undefined>(
    undefined,
  );
  const product1Data = useGetProductData({ product: products[0] });
  const product2Data = useGetProductData({ product: products[1] });

  const isProductsLoading = product1Data.isLoading || product2Data.isLoading;

  const productsData = [product1Data, product2Data];
  const selectedProductData = productsData[selectedProductIndex];

  const { product, sanityProduct, shopifyProduct, variants, subscriptions } =
    selectedProductData;

  const {
    slug,
    mostPopularVariantId,
    bestValueVariantId,
    media,
    bulletPoints,
    seeItInAction,
    disclaimer,
  } = product.product;

  const frontrowIds = useMemo(
    () =>
      products.map((item) => {
        const { frontrowMD: fm } = item.product;
        const enabled =
          env.NEXT_PUBLIC_FRONTROWMD_ENABLED &&
          !!fm?.productId &&
          fm?.isFrontrowBadgeEnabled;
        return enabled ? fm.productId : null;
      }),
    [products],
  );

  const selectedHasFrontrow = !!frontrowIds[selectedProductIndex];

  const { availableForSale } = shopifyProduct || {};

  const selectedVariantsIds = useMemo(
    () => variants?.map((item) => item.id) || [],
    [variants],
  );

  const selectedVariantOrSubscription = findMatchingVariantOrSub(
    variants,
    subscriptions,
    selected,
  );

  const productInfo = useMemo(
    () =>
      resolveProductInfo({
        sanityProduct,
        variants: shopifyProduct?.variants,
        selectedVariantsIds,
        productOverrides: product.productOverrides,
        images: media,
      }),
    [
      sanityProduct,
      shopifyProduct,
      selectedVariantsIds,
      product.productOverrides,
      media,
    ],
  );

  const shopifyProducts = useMemo(() => {
    if (isProductsLoading) return null;

    const bothProducts = [
      product1Data.shopifyProduct,
      product2Data.shopifyProduct,
    ];

    return bothProducts.every(Boolean)
      ? (bothProducts as ShopifyProduct[])
      : null;
  }, [
    isProductsLoading,
    product1Data.shopifyProduct,
    product2Data.shopifyProduct,
  ]);

  const { data: updatedProducts, isLoading: isUpdatedProductsLoading } =
    useProductsUpdatedPrices({
      blockId: _key,
      shopifyProducts,
      promoCodes,
    });

  const updatedProduct = updatedProducts?.[selectedProductIndex];

  const selectorOptions = useMemo(
    () =>
      products.map((item, index) => ({
        label: item.selector.label,
        value: index.toString(),
        image: item.selector.asset,
      })),
    [products],
  );

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: 'supplement-picker',
    defaultValue: '0',
    onChange: (value) => setSelectedProductIndex(Number(value)),
  });

  const group = getRootProps();

  const isPromoCodeVisible =
    !!promoCodes && promoCodes.length === 1 && amazonButton?.hasAmazonButton;

  return (
    <Grid
      id={_key}
      className="section--supplement-picker"
      my={{ base: borderWidthClamp, lg: '2xl' }}
    >
      {/* INFO: Use Hide and `display: none` to prevent FOUC before Hide
       * is mounted and can actually remove the child from the DOM. */}
      <Hide above="lg">
        <Box
          className="product_media"
          gridColumn="span 12"
          display={{ base: 'block', lg: 'none' }}
        >
          <ProductGalleryMobile
            media={productInfo.images}
            title={sanityProduct.title}
            isDoctorRecommended={sanityProduct.isDoctorRecommended}
            isLoading={isProductsLoading}
          />
        </Box>
      </Hide>
      <Grid.Inner gridRowGap={{ base: 'm', md: 'lg' }}>
        <Hide below="lg">
          <Box
            className="product_media"
            gridColumn="span 8"
            mr="5%"
            display={{ base: 'none', lg: 'block' }}
          >
            <ProductGalleryDesktop
              media={productInfo.images}
              title={sanityProduct.title}
              isDoctorRecommended={sanityProduct.isDoctorRecommended}
              isLoading={isProductsLoading}
            />
          </Box>
        </Hide>
        <Stack
          className="product_content"
          gap="m"
          gridColumn={{ base: '1/-1', lg: '9/-1' }}
          gridRow={{ base: 2, lg: 1 }}
          position="sticky"
          top="2xl"
          alignSelf="start"
        >
          <Stack gap="sm">
            <ProductIntro
              title={productInfo.title}
              subtitle={productInfo.subtitle}
              longDescription={productInfo.description}
              isMoneyBackGuarantee={sanityProduct.isMoneyBackGuarantee}
              showReviews
              showCustomerResults
              productSlugs={[slug]}
            />
            {bulletPoints && (
              <Box>
                <RichTextResolver
                  blocks={bulletPoints}
                  container={ProductBulletPoints}
                />
              </Box>
            )}
          </Stack>
          {isPromoCodeVisible && availableForSale && (
            <PromoCode title={productInfo.title} promoCode={promoCodes[0]} />
          )}
          {frontrowIds.some(Boolean) && (
            <Box display={selectedHasFrontrow ? 'block' : 'none'}>
              <Stack gap="m">
                <Divider />
                {frontrowIds.map((id, index) =>
                  id ? (
                    <Box
                      key={id}
                      display={
                        index === selectedProductIndex ? 'block' : 'none'
                      }
                    >
                      <ProductFrontrowMdWidget id={id} />
                    </Box>
                  ) : null,
                )}
                <Divider />
              </Stack>
            </Box>
          )}
          <Stack gap="2xs" py="4xs">
            <Text as="h3" size="title2">
              Format.{' '}
              <Text as="span" size="title2" color="blackAlpha.600">
                Same benefits, different formats.
              </Text>
            </Text>
            <SimpleGrid {...group} gap="2xs" columns={selectorOptions.length}>
              {selectorOptions.map((item) => (
                <RadioCard
                  key={item.value}
                  {...getRadioProps({ value: item.value })}
                  image={item.image}
                >
                  {item.label}
                </RadioCard>
              ))}
            </SimpleGrid>
          </Stack>
          <Stack className="product_configurator" gap="sm">
            <ProductConfiguratorRender
              isSupplementPicker
              slug={slug}
              shopifyProduct={updatedProduct}
              isLoading={isProductsLoading || isUpdatedProductsLoading}
              isPreorder={sanityProduct.isPreorder}
              mostPopularVariantId={mostPopularVariantId}
              bestValueVariantId={bestValueVariantId}
              selectedVariantsIds={selectedVariantsIds}
              ctaType={ctaType}
              amazonButton={amazonButton}
              asin={sanityProduct.asin}
              purchaseType={purchaseType}
              promoCodes={promoCodes}
              setSelectedVariant={setSelected}
              selectedVariant={selectedVariantOrSubscription}
            />
            {!(isProductsLoading || isUpdatedProductsLoading) && (
              <ProductCheckmarks
                productId={shopifyProduct?.id}
                isShippingInfoVisible
                isTruemedVisible={false}
              />
            )}
            {disclaimer && <ProductDisclaimer>{disclaimer}</ProductDisclaimer>}
          </Stack>
          {seeItInAction && !!seeItInAction.items?.length && (
            <ProductSeeItInAction {...seeItInAction} />
          )}
        </Stack>
      </Grid.Inner>
    </Grid>
  );
}
